import { Button, Input, Modal, ModalBody, ModalContent, ModalFooter, ModalHeader } from '@nextui-org/react'
import { FC, useEffect, useMemo, useState } from 'react'
import { MdPlace } from 'react-icons/md'
import Select from 'react-select'
import { StoreAddressInput, labelsApi, ClRegion } from '../api'
import { COURIER, COUNTRY, TERMINO_ETIQUETA_LOWER } from '../constants'

/**
 * Modal reutilizable para cargar la dirección principal de una tienda.
 *
 * En Chile (Blue Express) la comuna/localidad se eligen con selects en cascada
 * (Región → Comuna → Localidad) poblados desde /locations/cl, y se guardan los
 * códigos exactos que exige Blue Express (cod_region, cod_comuna, cod_localidad).
 * En el resto (DHL · México) se mantiene el ingreso por texto libre.
 */
type AddressForm = {
  calle: string, numero: string, colonia: string, ciudad: string, estado_region: string,
  codigo_postal: string, referencia: string, contacto_nombre: string, contacto_telefono: string,
  contacto_email: string,
  cod_region?: number | null, cod_comuna?: number | null, nom_comuna?: string | null, cod_localidad?: string | null, nom_localidad?: string | null
}

const isChile = COUNTRY === 'CL'

type Opt<V> = { value: V, label: string }

const AddressModal: FC<{
  target: { id_store: number, store: string, initial?: Partial<AddressForm> } | null
  onClose: () => void
  onSave: (input: StoreAddressInput) => Promise<void>
  confirmLabel?: string
}> = ({ target, onClose, onSave, confirmLabel = 'Guardar dirección' }) => {
  const empty: AddressForm = {
    calle: '', numero: '', colonia: '', ciudad: '', estado_region: '',
    codigo_postal: '', referencia: '', contacto_nombre: '', contacto_telefono: '', contacto_email: ''
  }
  const [f, setF] = useState(empty)
  const [saving, setSaving] = useState(false)
  const esEdicion = target?.initial !== undefined

  // Catálogo geográfico CL y selección en cascada.
  const [regions, setRegions] = useState<ClRegion[]>([])
  const [loadingGeo, setLoadingGeo] = useState(false)
  const [regionCod, setRegionCod] = useState<number | null>(null)
  const [comunaCod, setComunaCod] = useState<number | null>(null)
  const [localidadCod, setLocalidadCod] = useState<string | null>(null)

  // Carga el catálogo la primera vez que se abre el modal en Chile.
  useEffect(() => {
    if (!isChile || target === null || regions.length > 0) return
    setLoadingGeo(true)
    labelsApi.getLocations()
      .then(setRegions)
      .catch(() => { /* si falla, el usuario ve selects vacíos */ })
      .finally(() => setLoadingGeo(false))
  }, [target, regions.length])

  // Reinicia/precarga el formulario al abrir para otra tienda.
  useEffect(() => {
    setF({ ...empty, ...(target?.initial ?? {}) })
    setRegionCod(target?.initial?.cod_region ?? null)
    setComunaCod(target?.initial?.cod_comuna ?? null)
    setLocalidadCod(target?.initial?.cod_localidad ?? null)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [target])

  const region = useMemo(() => regions.find(r => r.codRegion === regionCod) ?? null, [regions, regionCod])
  const comuna = useMemo(() => region?.comunas.find(c => c.codComuna === comunaCod) ?? null, [region, comunaCod])
  const localidad = useMemo(() => comuna?.localidades.find(l => l.codLocalidad === localidadCod) ?? null, [comuna, localidadCod])

  if (target === null) return null
  const set = (k: keyof AddressForm) => (v: string): void => setF(prev => ({ ...prev, [k]: v }))

  // Al cambiar región: reinicia comuna y localidad.
  const onRegion = (cod: number | null): void => { setRegionCod(cod); setComunaCod(null); setLocalidadCod(null) }
  // Al cambiar comuna: preselecciona la localidad "principal" (la que coincide con
  // el nombre de la comuna) o la única disponible; si hay varias distintas, la deja vacía.
  const onComuna = (cod: number | null): void => {
    setComunaCod(cod)
    const c = region?.comunas.find(x => x.codComuna === cod) ?? null
    if (c === null) { setLocalidadCod(null); return }
    const principal = c.localidades.find(l => l.nomLocalidad === c.nomComuna)
    setLocalidadCod(c.localidades.length === 1 ? c.localidades[0].codLocalidad : (principal?.codLocalidad ?? null))
  }

  const regionOpts: Array<Opt<number>> = regions.map(r => ({ value: r.codRegion, label: r.nomRegion }))
  const comunaOpts: Array<Opt<number>> = (region?.comunas ?? []).map(c => ({ value: c.codComuna, label: c.nomComuna }))
  const localidadOpts: Array<Opt<string>> = (comuna?.localidades ?? []).map(l => ({ value: l.codLocalidad, label: l.nomLocalidad }))

  const obligatoriosOk = isChile
    ? (f.calle.trim() !== '' && region !== null && comuna !== null && localidad !== null &&
       f.contacto_nombre.trim() !== '' && f.contacto_telefono.trim() !== '')
    : (f.calle.trim() !== '' && f.ciudad.trim() !== '' && f.codigo_postal.trim() !== '' &&
       f.contacto_nombre.trim() !== '' && f.contacto_telefono.trim() !== '')

  const guardar = async (): Promise<void> => {
    setSaving(true)
    try {
      const input: StoreAddressInput = {
        id_store: target.id_store,
        calle: f.calle,
        numero: f.numero,
        colonia: f.colonia,
        ciudad: f.ciudad,
        estado_region: f.estado_region,
        codigo_postal: f.codigo_postal,
        referencia: f.referencia,
        contacto_nombre: f.contacto_nombre,
        contacto_telefono: f.contacto_telefono,
        contacto_email: f.contacto_email
      }
      if (isChile && region !== null && comuna !== null && localidad !== null) {
        // "ciudad" queda como la ciudad real (campo aparte); la comuna va en
        // nom_comuna/cod_comuna y la región en estado_region.
        input.estado_region = region.nomRegion
        input.cod_region = region.codRegion
        input.cod_comuna = comuna.codComuna
        input.nom_comuna = comuna.nomComuna
        input.cod_localidad = localidad.codLocalidad
        input.nom_localidad = localidad.nomLocalidad
      }
      await onSave(input)
    } finally { setSaving(false) }
  }

  // Estilos para que el menú del select se vea sobre el modal.
  const selectStyles = {
    menuPortal: (base: any) => ({ ...base, zIndex: 9999 }),
    control: (base: any) => ({ ...base, minHeight: 40, borderRadius: 12 })
  }

  return (
    <Modal isOpen={target !== null} onClose={onClose} size='2xl' scrollBehavior='inside'>
      <ModalContent>
        <ModalHeader className='flex flex-col gap-0'>
          <span className='text-[10px] uppercase tracking-widest text-[#37417f]/60 font-bold'>{esEdicion ? 'Editar dirección de retiro' : 'Agregar dirección de retiro'}</span>
          <span className='text-base font-bold text-[#151930]'>{target.store}</span>
        </ModalHeader>
        <ModalBody className='gap-3'>
          <div className='grid grid-cols-1 md:grid-cols-2 gap-3'>
            <Input size='sm' label='Calle' isRequired value={f.calle} onValueChange={set('calle')} />
            <Input size='sm' label='Número' value={f.numero} onValueChange={set('numero')} />

            {isChile
              ? (
                <>
                  <div className='flex flex-col gap-1'>
                    <label className='text-[11px] text-[#151930]/70 font-medium'>Región *</label>
                    <Select
                      options={regionOpts}
                      isLoading={loadingGeo}
                      placeholder='Selecciona región'
                      value={regionOpts.find(o => o.value === regionCod) ?? null}
                      onChange={(o) => onRegion(o?.value ?? null)}
                      menuPortalTarget={typeof document !== 'undefined' ? document.body : undefined}
                      styles={selectStyles}
                    />
                  </div>
                  <div className='flex flex-col gap-1'>
                    <label className='text-[11px] text-[#151930]/70 font-medium'>Comuna *</label>
                    <Select
                      options={comunaOpts}
                      isDisabled={region === null}
                      placeholder='Selecciona comuna'
                      value={comunaOpts.find(o => o.value === comunaCod) ?? null}
                      onChange={(o) => onComuna(o?.value ?? null)}
                      menuPortalTarget={typeof document !== 'undefined' ? document.body : undefined}
                      styles={selectStyles}
                    />
                  </div>
                  <div className='flex flex-col gap-1'>
                    <label className='text-[11px] text-[#151930]/70 font-medium'>Localidad *</label>
                    <Select
                      options={localidadOpts}
                      isDisabled={comuna === null}
                      placeholder='Selecciona localidad'
                      value={localidadOpts.find(o => o.value === localidadCod) ?? null}
                      onChange={(o) => setLocalidadCod(o?.value ?? null)}
                      menuPortalTarget={typeof document !== 'undefined' ? document.body : undefined}
                      styles={selectStyles}
                    />
                  </div>
                  <Input size='sm' label='Ciudad' value={f.ciudad} onValueChange={set('ciudad')} />
                  <Input size='sm' label='Referencia' value={f.referencia} onValueChange={set('referencia')} />
                </>
                )
              : (
                <>
                  <Input size='sm' label='Colonia' value={f.colonia} onValueChange={set('colonia')} />
                  <Input size='sm' label='Ciudad' isRequired value={f.ciudad} onValueChange={set('ciudad')} />
                  <Input size='sm' label='Estado / Región' value={f.estado_region} onValueChange={set('estado_region')} />
                  <Input size='sm' label='Código postal' isRequired value={f.codigo_postal} onValueChange={set('codigo_postal')} />
                  <Input size='sm' label='Referencia' value={f.referencia} onValueChange={set('referencia')} />
                </>
                )}

            <Input size='sm' label='Contacto (nombre)' isRequired value={f.contacto_nombre} onValueChange={set('contacto_nombre')} />
            <Input size='sm' label='Teléfono de contacto' isRequired value={f.contacto_telefono} onValueChange={set('contacto_telefono')} />
            <Input size='sm' type='email' label='Correo de contacto' value={f.contacto_email} onValueChange={set('contacto_email')} />
          </div>
          <p className='text-[11px] text-[#151930]/50'>El correo de contacto recibe las alertas de la tienda (sin custodia, sin sello, etc.).</p>
          <p className='text-[11px] text-[#151930]/50'>Los campos marcados son obligatorios para generar la {TERMINO_ETIQUETA_LOWER} de {COURIER}.</p>
        </ModalBody>
        <ModalFooter className='flex justify-end gap-2'>
          <Button variant='bordered' className='border-gray-200 text-[#151930]' onPress={onClose}>Cancelar</Button>
          <Button
            className='bg-[#151930] text-white font-semibold'
            isLoading={saving}
            isDisabled={!obligatoriosOk}
            startContent={saving ? undefined : <MdPlace className='text-sm' />}
            onPress={() => { void guardar() }}
          >
            {confirmLabel}
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  )
}

export default AddressModal
